import { useRef, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  decodeWorkflowExport,
  encodeWorkflowExport,
  retargetWorkflowExport,
  retargetedFileName,
  summarizeWorkflowExport,
  type WorkflowExportSummary,
} from '@lg/core';
import { errorMessage, saveFile } from '../ui/format';

const useStyles = makeStyles({
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  facts: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalXS,
  },
  mono: { fontFamily: tokens.fontFamilyMonospace },
});

const usd = (n: number, currency: string) =>
  `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${currency}`;

/**
 * Takes a workflow exported from the template company (Workflows → Export) and saves the same
 * file for the venue company: only the company changes, encoding and everything else stay as
 * exported. Runs in the browser; the file is never uploaded anywhere. Lists what the workflow
 * points at, so the admin can check it exists in the new company before importing.
 */
export function WorkflowExportRetarget({ legalEntityId }: { legalEntityId: string | null }) {
  const styles = useStyles();
  const input = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<{ fileName: string; summary: WorkflowExportSummary } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setError(null);
    setResult(null);
    try {
      if (!legalEntityId) throw new Error('Enter the legal entity ID first');
      const decoded = decodeWorkflowExport(new Uint8Array(await file.arrayBuffer()));
      const summary = summarizeWorkflowExport(decoded.xml);
      const bytes = encodeWorkflowExport(retargetWorkflowExport(decoded.xml, legalEntityId), decoded);
      const fileName = retargetedFileName(file.name, legalEntityId);
      saveFile({ fileName, mimeType: 'application/xml', bytes });
      setResult({ fileName, summary });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const s = result?.summary;
  return (
    <Card className={styles.card}>
      <Subtitle1 as="h2">Copy a D365 workflow export to {legalEntityId ?? 'the venue'}</Subtitle1>
      <Body1>
        Export the workflow in the template company (Workflows → select it → Export), choose the file here,
        and the same file is saved for {legalEntityId ?? 'the venue company'}. Only the company changes;
        routing, conditions, approvers and the file encoding stay exactly as exported. Import it in{' '}
        {legalEntityId ?? 'the venue company'} with Workflows → Import.
      </Body1>
      <div>
        <Button disabled={!legalEntityId} onClick={() => input.current?.click()}>
          Choose workflow export (.xml)
        </Button>
        <input
          ref={input}
          type="file"
          accept=".xml,text/xml,application/xml"
          aria-label="Workflow export file"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {s && result && (
        <>
          <MessageBar intent="success">
            <MessageBarBody>
              Saved <b>{result.fileName}</b>: {s.name} {s.version} moved from {s.company.toUpperCase()} to{' '}
              {legalEntityId}.
            </MessageBarBody>
          </MessageBar>
          <Caption1>
            Check these exist in {legalEntityId} before importing and submitting a test journal:
          </Caption1>
          <div className={styles.facts}>
            <Caption1>Workflow type</Caption1>
            <Caption1 className={styles.mono}>
              {s.category} ({s.template})
            </Caption1>
            {s.conditions.length > 0 && (
              <>
                <Caption1>Conditions</Caption1>
                <Caption1>
                  {s.conditions.map((c) => `${c.operator} ${usd(c.amount, c.currency)}`).join(', ')}
                </Caption1>
              </>
            )}
            <Caption1>Assigned by</Caption1>
            <Caption1>{s.assignmentTypes.join(', ') || 'none'}</Caption1>
            {s.users.length > 0 && (
              <>
                <Caption1>Named users</Caption1>
                <Caption1 className={styles.mono}>{s.users.join(', ')}</Caption1>
              </>
            )}
            {s.userGroups.length > 0 && (
              <>
                <Caption1>User groups</Caption1>
                <Caption1 className={styles.mono}>{s.userGroups.join(', ')}</Caption1>
              </>
            )}
            {s.calendars.length > 0 && (
              <>
                <Caption1>Work calendars</Caption1>
                <Caption1 className={styles.mono}>{s.calendars.join(', ')} (company-specific)</Caption1>
              </>
            )}
            {s.owner && (
              <>
                <Caption1>Owner</Caption1>
                <Caption1 className={styles.mono}>{s.owner}</Caption1>
              </>
            )}
          </div>
          <Caption1>
            After the import, D365 gives the workflow a new ID in {legalEntityId}: select it on the journal
            name (Journal names → Approval → Workflow) before submitting a journal.
          </Caption1>
        </>
      )}
    </Card>
  );
}
